import { Service } from '@angular/core';

/**
 * Service to manage browser location and navigation. Bypasses Angular's router for direct URL manipulation.
 * Useful for scenarios where you need to navigate to a URL without going through Angular's routing system.
 *
 * This service exists mainly to make stubbing easier in tests.
 */
@Service()
export class Location {
  /**
   * Gets the current URL (including the hash and query string).
   */
  get href(): string {
    return window.location.href;
  }

  /**
   * Gets the current URL path (excluding the hash and query string).
   */
  get hash(): string {
    return window.location.hash;
  }

  /**
   * Sets the hash part of the URL (the part after the #).
   * @param href
   */
  set hash(href: string) {
    window.location.hash = href;
  }

  /**
   * Performs a direct browser navigation to the specified URL, bypassing Angular router.
   *
   * @param url The URL to navigate to (can be internal or external)
   * @param openInNewWindow Whether to open the URL in a new browser tab/window. Default is false.
   */
  openWindow(url: string, openInNewWindow: boolean = false): void {
    window.open(url, openInNewWindow ? '_blank' : '_self');
  }

  /**
   * Performs a direct browser navigation to the specified URL, bypassing Angular router.
   * Adds the URL to the browser's history stack, allowing the user to navigate back to the previous page.
   * @param url
   */
  assign(url: string): void {
    window.location.assign(url);
  }

  /**
   * Performs a direct browser navigation to the specified URL, bypassing Angular router.
   * This replaces the current page in the browser's history stack, meaning the user cannot navigate back to the previous page.
   * @param url
   */
  replace(url: string): void {
    window.location.replace(url);
  }
}
